import { IconButton, Tag, Tooltip } from '@ads/components-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClassModuleListItemViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

import { CLASS_MODULE_PAGE_SIZE } from '../../../hooks/useClassModuleList';
import {
  formatModuleDate,
  MODALITY_LABEL_KEY,
  MODALITY_TAG_STATUS,
} from '../../../utils/classModuleListFormat';

export interface ClassModuleTableProps {
  readonly data: readonly ClassModuleListItemViewModel[];
  readonly isLoading: boolean;
  /** Página atual (1-indexed). */
  readonly currentPage: number;
  /** Há uma próxima página (inferido por página cheia quando não há total). */
  readonly hasNextPage: boolean;
  /** Total de páginas, quando o backend informa (envelope paginado). */
  readonly totalPages?: number | undefined;
  /** Total de registros, quando o backend informa (envelope paginado). */
  readonly totalCount?: number | undefined;
  readonly onPageChange: (page: number) => void;
  readonly onEdit: (id: number) => void;
  readonly onDelete: (module: ClassModuleListItemViewModel) => void;
}

/**
 * Tabela de módulos de aula. Renderiza descrição, modalidade (Tag), datas e ações
 * (editar/excluir), delegando loading/empty/paginação ao `DataTable`.
 *
 * Usa `totalPages`/`totalCount` do backend quando informados; na ausência deles,
 * deriva a paginação (habilita "Próxima" enquanto houver página cheia).
 */
export function ClassModuleTable({
  data,
  isLoading,
  currentPage,
  hasNextPage,
  totalPages,
  totalCount,
  onPageChange,
  onEdit,
  onDelete,
}: ClassModuleTableProps) {
  const { t } = useTranslation('classModuleManagement');

  const columns = useMemo<ColumnDef<ClassModuleListItemViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'description',
        header: t('table.columns.description'),
        cell: info => (
          <Tooltip title={(info.getValue() as string) ?? ''} align="start">
            <span className="ts-paragraph-xxs !font-medium line-clamp-2">
              {(info.getValue() as string) ?? ''}
            </span>
          </Tooltip>
        ),
      },
      {
        id: 'modality',
        accessorKey: 'modality',
        header: t('table.columns.modality'),
        cell: ({ row }) => {
          const { modality } = row.original;
          if (modality === undefined) return null;
          return (
            <Tag
              label={t(MODALITY_LABEL_KEY[modality] as 'general.modalityOptions.distance')}
              size="md"
              status={MODALITY_TAG_STATUS[modality]}
              custom={{ text: 'text-nowrap' }}
            />
          );
        },
      },
      {
        id: 'dateStart',
        accessorKey: 'dateStart',
        header: t('table.columns.dateStart'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs">{formatModuleDate(row.original.dateStart)}</span>
        ),
      },
      {
        id: 'dateFinish',
        accessorKey: 'dateFinish',
        header: t('table.columns.dateFinish'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs">{formatModuleDate(row.original.dateFinish)}</span>
        ),
      },
      {
        id: 'actions',
        header: t('table.columns.actions'),
        enableSorting: false,
        cell: ({ row }) => {
          const id = row.original.id ?? 0;
          return (
            <div className="flex items-center gap-quark">
              <IconButton
                icon={PencilSquareIcon}
                ariaLabel={t('table.edit')}
                hierarchy="tertiary"
                size="md"
                onClick={() => onEdit(id)}
              />
              <IconButton
                icon={TrashIcon}
                ariaLabel={t('table.delete')}
                hierarchy="tertiary"
                className="!text-base-danger-600"
                size="md"
                onClick={() => onDelete(row.original)}
              />
            </div>
          );
        },
      },
    ],
    [t, onEdit, onDelete],
  );

  // Usa a metadata do envelope quando disponível; senão deriva para navegação prev/next.
  const resolvedTotalPages = totalPages ?? (hasNextPage ? currentPage + 1 : currentPage);
  const resolvedTotalCount = totalCount ?? (currentPage - 1) * CLASS_MODULE_PAGE_SIZE + data.length;

  return (
    <DataTable
      data={data as ClassModuleListItemViewModel[]}
      columns={columns}
      isLoading={isLoading}
      totalCount={resolvedTotalCount}
      currentPage={currentPage}
      totalPages={resolvedTotalPages}
      pageSize={CLASS_MODULE_PAGE_SIZE}
      onPageChange={onPageChange}
      emptyStateMessage={t('table.empty')}
      enableSorting
    />
  );
}
